import { Grid } from '@mui/material';
import {
  HSpacer,
  RoutalAvatar,
  RoutalDialog,
  RoutalSelect,
  RoutalSelectMultiSelectOption,
  VSpacer,
} from '@nx-smartmonkey/ui';
import { useEffect, useState } from 'react';

import { OrganizationUser } from '../../../domain/organization/OrganizationUser';
import { Project } from '../../../domain/project/Project';
import { ProjectUserRoles } from '../../../domain/project/ProjectUserRoles';
import useCreateProjectUser from '../../../hooks/project/useCreateProjectUser';
import Button from '../../../react-components/Button';
import Text from '../../../react-components/Text';
import { getUploadAsBase64 } from '../../../services/requests';

type OrganizationProjectAddUserDialogProps = {
  selectedProject: Project;
  availableUsers?: OrganizationUser[];
  isOpen?: boolean;
  onClose: (...args: any[]) => any;
};

export const OrganizationProjectAddUserDialog = ({
  selectedProject,
  availableUsers,
  isOpen = false,
  onClose,
}: OrganizationProjectAddUserDialogProps) => {
  const { run: addProjectUser, success: createUserSuccess, error: createUserError } = useCreateProjectUser();
  const [userId, setUserId] = useState<string | undefined>(
    availableUsers && availableUsers.length > 0 ? availableUsers[0].id : undefined
  );
  const [role, setRole] = useState<ProjectUserRoles>(`project_manager`);
  const [disabledSubmitButton, setDisabledSubmitButton] = useState<boolean>(false);

  const closeDialog = (refetch: boolean) => {
    setDisabledSubmitButton(false);
    onClose(refetch ?? false);
  };

  useEffect(() => {
    if (createUserSuccess) {
      closeDialog(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createUserSuccess]);

  useEffect(() => {
    if (createUserError) {
      setDisabledSubmitButton(false);
    }
  }, [createUserError]);

  useEffect(() => {
    const availableIds = (availableUsers ?? []).map((availableUser) => availableUser.id);
    if (isOpen && (!userId || !availableIds.includes(userId))) {
      setUserId(availableIds.length > 0 ? availableIds[0] : undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const handleAddUser = () => {
    if (userId) {
      setDisabledSubmitButton(true);
      addProjectUser({
        userId,
        role,
        project: selectedProject,
      });
    }
  };

  return (
    <RoutalDialog
      titleContent={<Text variant="h6" intlId="projects.settings.members.add.title" />}
      dialogProps={{
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
      }}
      open={!!isOpen}
      onCloseDialog={closeDialog}
      showFooter={false}
    >
      <Grid container direction="column">
        <Grid container direction="row" wrap="nowrap" justifyContent="space-between">
          <Grid item container direction="column">
            <Text intlId="projects.settings.members.add.select_user" />

            <VSpacer small />

            <RoutalSelect
              id="add-user-select"
              popoverProps={{
                options: [
                  ...(availableUsers ?? []).map((u): RoutalSelectMultiSelectOption => {
                    return {
                      key: u.id,
                      onClick: () => setUserId(u.id),
                      content: (
                        <Grid container alignItems="center">
                          <RoutalAvatar
                            url={u.avatar_url}
                            getUploadAsBase64={getUploadAsBase64}
                            label={`${u.first_name} ${u.last_name}`}
                            labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                            containerStyles={{ width: `24px`, height: `24px` }}
                          />
                          <span style={{ marginLeft: `8px` }}>{`${u.first_name} ${u.last_name}`}</span>
                        </Grid>
                      ),
                    };
                  }),
                ],
              }}
            >
              <>
                {(() => {
                  const selectedUser = (availableUsers ?? []).find((_user) => _user.id === userId);
                  if (!selectedUser) return null;

                  return (
                    <Grid
                      container
                      alignItems="center"
                      style={{ width: `100%`, textAlign: `start`, flexWrap: `unset` }}
                    >
                      <Grid item>
                        <RoutalAvatar
                          url={selectedUser.avatar_url}
                          getUploadAsBase64={getUploadAsBase64}
                          label={`${selectedUser.first_name} ${selectedUser.last_name}`}
                          labelStyles={{ fontSize: `10px`, lineHeight: 2 }}
                          containerStyles={{ width: `24px`, height: `24px` }}
                        />
                      </Grid>

                      <Grid item>
                        <Text variant="body" style={{ cursor: `pointer`, marginLeft: `8px` }}>
                          {`${selectedUser.first_name} ${selectedUser.last_name}`}
                        </Text>
                      </Grid>
                    </Grid>
                  );
                })()}
              </>
            </RoutalSelect>
          </Grid>

          <HSpacer medium />

          <Grid item container direction="column">
            <Text intlId="projects.settings.members.add.project_role" />

            <VSpacer small />

            <RoutalSelect
              id="add-role-select"
              disabled={disabledSubmitButton}
              popoverProps={{
                options: [
                  {
                    key: `project_manager`,
                    listItemStyles: {
                      whiteSpace: `normal` as any,
                      overflow: `auto`,
                      color: `#929599`,
                      height: `100%`,
                      padding: `8px 16px`,
                      ...(role === `project_manager` ? { backgroundColor: `#f8671d14` } : {}),
                    },
                    content: (
                      <Grid container direction="column" wrap="nowrap">
                        <Text
                          style={{ cursor: `pointer` }}
                          variant="body-medium"
                          intlId="projects.settings.roles.manager"
                        />
                        <Text
                          style={{ cursor: `pointer` }}
                          variant="body"
                          intlId="projects.settings.roles.manager.description"
                        />
                      </Grid>
                    ),
                    onClick: () => setRole(`project_manager`),
                  },
                  {
                    key: `project_user`,
                    listItemStyles: {
                      whiteSpace: `normal` as any,
                      overflow: `auto`,
                      color: `#929599`,
                      height: `100%`,
                      padding: `8px 16px`,
                      ...(role === `project_user` ? { backgroundColor: `#f8671d14` } : {}),
                    },
                    content: (
                      <Grid container direction="column" wrap="nowrap">
                        <Text
                          style={{ cursor: `pointer` }}
                          variant="body-medium"
                          intlId="projects.settings.roles.user"
                        />
                        <Text
                          style={{ cursor: `pointer` }}
                          variant="body"
                          intlId="projects.settings.roles.user.description"
                        />
                      </Grid>
                    ),
                    onClick: () => setRole(`project_user`),
                  },
                ],
              }}
            >
              <Text
                variant="body"
                style={{ cursor: `pointer`, width: `100%`, textAlign: `start` }}
                intlId={`projects.settings.roles.${role === `project_manager` ? `manager` : `user`}`}
              />
            </RoutalSelect>
          </Grid>
        </Grid>

        <VSpacer large />

        <Grid container direction="row" alignItems="center" justifyContent="flex-end">
          <Button
            onClick={() => closeDialog(false)}
            variant="flat"
            styleType="secondary"
            intlId="settings.buttoncancel"
          />
          <HSpacer small />
          <Button
            type="submit"
            styleType="primary"
            disabled={disabledSubmitButton || !userId}
            intlId="settings.buttonadd"
            onClick={handleAddUser}
          />
        </Grid>

        <VSpacer small />
      </Grid>
    </RoutalDialog>
  );
};
